import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency, yearOf } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import { fmtDate, toRSD } from '../lib/helpers';

// ============================================================
// KPO KNJIGA — Knjiga o ostvarenom prometu paušalno oporezovanih obveznika
// Zvanični dokument: ostaje na srpskom bez obzira na jezik aplikacije.
// Kolone kao na zvaničnom obrascu: 1 redni broj · 2 datum i opis knjiženja ·
//   PRIHOD OD DELATNOSTI: 3 od prodaje proizvoda · 4 od izvršenih usluga ·
//   5 svega prihodi od delatnosti (3+4). Na dnu: Sastavio i Odgovorno lice.
// ============================================================

const num = (n) => new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Fakture se vode kao usluge; ako dokument ima oznaku da je prodaja proizvoda, ide u kolonu 3
const splitRevenue = (inv) => {
  const total = toRSD(inv);
  if (inv.revenue_kind === 'proizvod') return { products: total, services: 0, total };
  return { products: 0, services: total, total };
};

export default function KpoBook() {
  const { activeAgencyId, year, profile } = useAgency();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeAgencyId) return;
    supabase
      .from('invoices')
      .select('*')
      .eq('agency_id', activeAgencyId)
      .neq('document_kind', 'profaktura')
      .order('issue_date', { ascending: true })
      .then(({ data }) => {
        // Isti datum: po rednom broju fakture (01/2026 pre 02/2026)
        const lead = (n) => parseInt(String(n || '').match(/^\s*(\d+)/)?.[1] || '0', 10);
        const sorted = [...(data || [])].sort((x, y) =>
          String(x.issue_date).localeCompare(String(y.issue_date)) || lead(x.invoice_number) - lead(y.invoice_number));
        setInvoices(sorted);
        setLoading(false);
      });
  }, [activeAgencyId]);

  // Knjiga ide rastuće od broja 1 za izabranu kalendarsku godinu (bira se u levom meniju)
  const rows = invoices
    .filter((i) => yearOf(i.issue_date) === year)
    .map((inv, idx) => ({ inv, rb: idx + 1, ...splitRevenue(inv) }));

  const sumProducts = rows.reduce((s, r) => s + r.products, 0);
  const sumServices = rows.reduce((s, r) => s + r.services, 0);
  const sumTotal = sumProducts + sumServices;

  const p = profile || {};
  const seat = [[p.street, p.street_number].filter(Boolean).join(' '), [p.postal_code, p.city].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const activity = p.activity_code ? `${p.activity_code}${p.activity_name ? ' — ' + p.activity_name : ''}` : '';
  const header = [
    ['PIB', p.pib],
    ['Obveznik', p.owner_name],
    ['Firma-radnja', p.company_name],
    ['Sedište', seat],
    ['Šifra poreskog obveznika', p.taxpayer_code],
    ['Šifra delatnosti', activity],
  ];
  // Šifra poreskog obveznika se ne unosi na stranici Agencija, pa ne ulazi u upozorenje
  const missing = header
    .filter(([k, v]) => !v && k !== 'Šifra poreskog obveznika')
    .map(([k]) => k);

  // Štampa: A4 položeno, bez menija i dugmića
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'kpo-print-style';
    style.innerHTML = `
      @page { size: A4 landscape; margin: 12mm; }
      @media print {
        .app-main { margin-left: 0 !important; }
        .kpo-actions-col { display: none !important; }
        .kpo-sheet { border: none !important; padding: 0 !important; }
        .kpo-sheet, .kpo-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .kpo-signatures { break-inside: avoid; page-break-inside: avoid; }
      }
    `;
    document.head.appendChild(style);
    const prevTitle = document.title;
    document.title = `FinArt Studio KPO knjiga ${year}${p.company_name ? ' - ' + p.company_name : ''}`;
    return () => {
      const s = document.getElementById('kpo-print-style');
      if (s) s.remove();
      document.title = prevTitle;
    };
  }, [year, p.company_name]);

  if (loading) return <div style={styles.loading}>{t('loadingGeneric')}</div>;

  return (
    <div>
      <div style={styles.headerRow} className="no-print">
        <h1 style={styles.heading}>KPO knjiga <span style={styles.yearTag}>{year}</span></h1>
        <button onClick={() => window.print()} style={styles.printBtn}>
          <Icon name="printer" size={16} />
          {t('printPdf')}
        </button>
      </div>

      {missing.length > 0 && (
        <div style={styles.warnBox} className="no-print">
          <Icon name="building" size={18} color="var(--gold-600)" />
          <span style={{ flex: 1 }}>
            Za potpuno zaglavlje KPO knjige nedostaje: <strong>{missing.join(', ')}</strong>.
          </span>
          <button onClick={() => navigate('/agencija')} style={styles.warnBtn}>Dopuni na stranici Agencija</button>
        </div>
      )}

      <div style={styles.sheet} className="kpo-sheet">
        <div style={styles.docTitle}>KNJIGA O OSTVARENOM PROMETU PAUŠALNO OPOREZOVANIH OBVEZNIKA</div>
        <div style={styles.docSub}>Obrazac KPO · za {year}. godinu</div>

        <div style={styles.headerGrid} className="kpo-head-grid">
          {header.map(([k, v]) => (
            <div key={k} style={styles.headerCell}>
              <div style={styles.headerLabel}>{k}</div>
              <div style={{ ...styles.headerValue, ...(v ? {} : styles.headerEmpty) }}>{v || '—'}</div>
            </div>
          ))}
        </div>

        {rows.length === 0 ? (
          <div style={styles.empty}>Nema evidentiranog prometa za {year}. godinu.</div>
        ) : (
          <>
          {/* Telefon: svaki red knjige kao kratka kartica (štampa uvek koristi tabelu) */}
          <div style={styles.mList} className="kpo-mobile">
            {rows.map(({ inv, rb, products, services, total }) => (
              <button key={inv.id} onClick={() => navigate(`/fakturisanje/${inv.id}/pregled`)} style={styles.mRow}>
                <span style={styles.mRb}>{rb}</span>
                <span style={styles.mText}>
                  <span style={styles.mTop}>
                    <span>{fmtDate(inv.issue_date)}</span>
                    {inv.invoice_number && <span style={styles.invNo}>{inv.invoice_number}</span>}
                    {inv.document_kind === 'fiskalni' && <span style={styles.kindTag}>fiskalni</span>}
                  </span>
                  {inv.client && <span style={styles.mClient}>{inv.client}</span>}
                </span>
                <span style={styles.mAmount}>
                  <span style={styles.mTotal}>{num(total)}</span>
                  <span style={styles.mKind}>{products > 0 && services > 0 ? 'proizvodi i usluge' : products > 0 ? 'proizvodi (3)' : 'usluge (4)'}</span>
                </span>
              </button>
            ))}
            <div style={styles.mSum}>
              <div style={styles.mSumTitle}>Ukupno za {year}. godinu</div>
              <div style={styles.mSumRow}><span>Od prodaje proizvoda (3)</span><strong>{num(sumProducts)}</strong></div>
              <div style={styles.mSumRow}><span>Od izvršenih usluga (4)</span><strong>{num(sumServices)}</strong></div>
              <div style={{ ...styles.mSumRow, ...styles.mSumFinal }}><span>Svega prihodi (3+4)</span><strong>{num(sumTotal)}</strong></div>
            </div>
          </div>

          <div style={styles.tableWrap} className="kpo-desktop">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ ...styles.th, width: 64 }}>Redni broj</th>
                  <th rowSpan={2} style={styles.th}>Datum i opis knjiženja</th>
                  <th colSpan={2} style={styles.thGroup}>
                    <div style={styles.thGroupLabel}>Prihod od delatnosti</div>
                  </th>
                  <th rowSpan={2} style={{ ...styles.th, ...styles.thCenter, width: 170 }}>Svega prihodi od delatnosti (3+4)</th>
                  <th rowSpan={2} style={{ ...styles.th, width: 52 }} className="kpo-actions-col" aria-label="Akcije" />
                </tr>
                <tr>
                  <th style={{ ...styles.thSub, ...styles.thCenter }}>Od prodaje proizvoda</th>
                  <th style={{ ...styles.thSub, ...styles.thCenter }}>Od izvršenih usluga</th>
                </tr>
                <tr>
                  {['1', '2', '3', '4', '5'].map((c, i) => (
                    <th key={c} style={{ ...styles.colNo, ...(i >= 2 ? styles.thCenter : {}) }}>{c}</th>
                  ))}
                  <th style={styles.colNo} className="kpo-actions-col" />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ inv, rb, products, services, total }) => (
                  <tr key={inv.id}>
                    <td style={{ ...styles.td, color: 'var(--ink-500)' }}>{rb}</td>
                    <td style={styles.td}>
                      <div style={styles.docLine}>
                        <span>{fmtDate(inv.issue_date)}</span>
                        {inv.invoice_number && <span style={styles.invNo}>{inv.invoice_number}</span>}
                        {inv.document_kind === 'fiskalni' && <span style={styles.kindTag}>fiskalni račun</span>}
                        {inv.document_kind === 'pazar' && <span style={styles.kindTag}>dnevni pazar</span>}
                      </div>
                      {inv.client && <div style={styles.client}>{inv.client}</div>}
                    </td>
                    <td style={{ ...styles.td, ...styles.num }}>{num(products)}</td>
                    <td style={{ ...styles.td, ...styles.num }}>{num(services)}</td>
                    <td style={{ ...styles.td, ...styles.num, fontWeight: 700 }}>{num(total)}</td>
                    <td style={styles.td} className="kpo-actions-col">
                      <button
                        onClick={() => navigate(`/fakturisanje/${inv.id}/pregled`)}
                        style={styles.viewBtn}
                        aria-label={`Otvori dokument ${inv.invoice_number || ''}`}
                        title="Otvori dokument"
                      >
                        <Icon name="fileText" size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={styles.totalLabel}>UKUPNO ZA {year}. GODINU</td>
                  <td style={{ ...styles.totalValue, ...styles.num }}>{num(sumProducts)}</td>
                  <td style={{ ...styles.totalValue, ...styles.num }}>{num(sumServices)}</td>
                  <td style={{ ...styles.totalValue, ...styles.num }}>{num(sumTotal)}</td>
                  <td style={styles.totalValue} className="kpo-actions-col" />
                </tr>
              </tfoot>
            </table>
          </div>
          </>
        )}

        <div style={styles.footNote}>Iznosi su u dinarima. Dokumenti u stranoj valuti preračunati su po srednjem kursu NBS sa dokumenta.</div>

        <style>{`
          .kpo-mobile { display: none; }
          @media (max-width: 760px) {
            .kpo-desktop { display: none !important; }
            .kpo-mobile { display: block !important; }
            .kpo-sheet { padding: 16px !important; border-radius: 16px !important; }
            .kpo-head-grid { grid-template-columns: 1fr 1fr !important; }
            .kpo-signatures { gap: 24px !important; }
          }
          @media print {
            .kpo-desktop { display: block !important; }
            .kpo-mobile { display: none !important; }
            .kpo-head-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
        `}</style>

        <div style={styles.sigRow} className="kpo-signatures">
          <div style={styles.sigBox}>
            <div style={styles.sigLabel}>Sastavio</div>
            <div style={styles.sigLine} />
          </div>
          <div style={styles.sigBox}>
            <div style={styles.sigLabel}>Odgovorno lice</div>
            <div style={styles.sigLine} />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: 40, textAlign: 'center', color: 'var(--ink-500)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  heading: { fontSize: 22 },
  yearTag: { fontSize: 14, fontWeight: 700, color: 'var(--ink-700)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '3px 9px', marginLeft: 6, verticalAlign: 'middle' },
  printBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
  warnBox: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--gold-100)', border: '1px solid var(--gold-300)', color: 'var(--ink-900)', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 16 },
  warnBtn: { border: '1px solid var(--gold-600)', background: 'var(--surface)', color: 'var(--gold-600)', borderRadius: 10, padding: '8px 12px', fontWeight: 700, fontSize: 12 },
  sheet: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: 24 },
  docTitle: { fontSize: 15, fontWeight: 800, letterSpacing: '0.02em', color: 'var(--ink-900)', textAlign: 'center' },
  docSub: { fontSize: 12, color: 'var(--ink-500)', textAlign: 'center', marginTop: 4, marginBottom: 20 },
  headerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 },
  headerCell: { padding: '10px 14px', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', marginRight: -1, marginBottom: -1 },
  headerLabel: { fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 },
  headerValue: { fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' },
  headerEmpty: { color: 'var(--ink-300)', fontWeight: 500 },
  empty: { textAlign: 'center', padding: '40px 20px', color: 'var(--ink-500)', fontSize: 14 },
  tableWrap: { overflowX: 'auto', border: '1px solid var(--line)', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 },
  th: { textAlign: 'left', padding: '12px 14px 6px', background: 'var(--paper)', fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.04em', verticalAlign: 'bottom' },
  // "Prihod od delatnosti": naslov na sredini, a ispod njega kratka linija koja obuhvata samo kolone 3 i 4
  thGroup: { padding: '12px 14px 0', background: 'var(--paper)', verticalAlign: 'bottom' },
  thGroupLabel: { textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.04em', paddingBottom: 7, borderBottom: '1.5px solid var(--ink-300)' },
  thSub: { padding: '8px 14px 6px', background: 'var(--paper)', fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '0.04em', verticalAlign: 'bottom' },
  thCenter: { textAlign: 'center' },
  colNo: { textAlign: 'left', padding: '0 14px 8px', background: 'var(--paper)', borderBottom: '1px solid var(--line)', fontSize: 11, fontWeight: 600, color: 'var(--ink-500)' },
  num: { textAlign: 'right', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid var(--line)', color: 'var(--ink-900)', verticalAlign: 'top' },
  docLine: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 600 },
  invNo: { fontSize: 12, fontWeight: 700, color: 'var(--teal-800)', background: 'var(--teal-100)', borderRadius: 6, padding: '1px 7px' },
  kindTag: { fontSize: 11, fontWeight: 700, color: 'var(--gold-600)', background: 'var(--gold-100)', borderRadius: 6, padding: '1px 7px' },
  client: { fontSize: 12, color: 'var(--ink-500)', marginTop: 3 },
  viewBtn: { width: 34, height: 34, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 9, color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  totalLabel: { padding: '14px', fontWeight: 800, fontSize: 12, letterSpacing: '0.04em', color: 'var(--ink-900)', background: 'var(--paper)' },
  totalValue: { padding: '14px', fontWeight: 800, color: 'var(--ink-900)', background: 'var(--paper)' },
  mList: { border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' },
  mRow: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 12px', border: 'none', borderBottom: '1px solid var(--line)', background: 'var(--surface)', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' },
  mRb: { width: 28, height: 28, borderRadius: 8, background: 'var(--paper)', color: 'var(--ink-500)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  mText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  mTop: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13, fontWeight: 600, color: 'var(--ink-900)' },
  mClient: { fontSize: 12.5, color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mAmount: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 },
  mTotal: { fontSize: 14, fontWeight: 800, color: 'var(--ink-900)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  mKind: { fontSize: 11, color: 'var(--ink-500)' },
  mSum: { background: 'var(--paper)', padding: '12px 14px' },
  mSumTitle: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-700)', marginBottom: 6 },
  mSumRow: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, color: 'var(--ink-700)', padding: '3px 0' },
  mSumFinal: { borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 8, fontSize: 15, color: 'var(--ink-900)', fontWeight: 700 },
  footNote: { fontSize: 11, color: 'var(--ink-500)', marginTop: 14 },
  sigRow: { display: 'flex', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap', marginTop: 48 },
  sigBox: { flex: '0 1 280px', minWidth: 200 },
  sigLabel: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 36 },
  sigLine: { borderBottom: '1px solid var(--ink-500)' },
};
